/**
 * PURPOSE: Elegir los relevistas (postas 1-4) de un equipo de posta.
 * RESPONSIBILITIES: Un selector con buscador por posta entre TODOS los deportistas acreditados de la institución en el
 *   deporte (los ya inscritos en la posta primero, luego el resto alfabético; sin distinguir mayúsculas ni tildes), con
 *   el género (V/D) y la categoría; un atleta elegido en otra posta del equipo queda deshabilitado. Si la lista del
 *   servidor no carga, usa los `members` del equipo. Devuelve `legs` completas: `[{ entryId, leg }]`; el servidor
 *   valida que cada uno sea de la misma institución, evento y deporte.
 *
 *   SOLO UNA PISTA. El orden mixto sugerido se muestra, no se impone: la regla la valida el servidor.
 * COLLABORATORS: RelayGrid.jsx.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/grids/RelayLegsModal.jsx
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import Button from "@mui/joy/Button";
import { FiInfo } from "react-icons/fi";

import { getAthleticsRelayCandidates } from "../../../../services/athleticsResultsService.js";
import { selectClass } from "../ui.jsx";

const LEGS = [1, 2, 3, 4];
const MIXED_HINT = { 1: "V", 2: "D", 3: "V", 4: "D" };
const GENDER_LABEL = { V: "Varón", D: "Dama" };

const fold = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/** Select con buscador: escribir filtra por nombre sin distinguir mayúsculas ni tildes. */
const SearchableSelect = ({ value, options, disabled, onChange, ariaLabel, className }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const needle = fold(query).trim();
  const visible = needle ? options.filter((option) => fold(option.label).includes(needle)) : options;

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1">
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setQuery("");
          setOpen((current) => !current);
        }}
        className={`${className} w-full truncate text-left`}
      >
        {selected ? selected.label : "Sin asignar"}
      </button>
      {open ? (
        <div className="absolute left-0 right-0 z-50 mt-1 rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar deportista..."
            aria-label="Buscar deportista"
            className="mb-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm outline-none focus:border-indigo-500"
          />
          <ul role="listbox" className="max-h-56 overflow-y-auto text-sm">
            <li>
              <button
                type="button"
                className="w-full rounded px-2 py-1 text-left text-slate-500 hover:bg-slate-100"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
              >
                Sin asignar
              </button>
            </li>
            {visible.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  disabled={option.disabled}
                  className={`w-full rounded px-2 py-1 text-left hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent ${
                    option.value === value ? "bg-indigo-50 font-bold" : ""
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                  {option.disabled && option.value !== value ? " · ya elegido" : ""}
                </button>
              </li>
            ))}
            {!visible.length ? <li className="px-2 py-1 text-slate-400">Sin resultados</li> : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const RelayLegsModal = ({ open, team, pruebaId, legs, mixed, readOnly, onClose, onConfirm }) => {
  const { idevent, idsport } = useParams();
  const teamMembers = useMemo(
    () => (Array.isArray(team?.members) ? team.members.filter((member) => member?.entryId != null) : []),
    [team]
  );
  const [candidates, setCandidates] = useState(null);
  const [selection, setSelection] = useState({});

  // Todos los acreditados de la institución en el deporte; si falla, se usan los inscritos en la posta.
  useEffect(() => {
    if (!open || !team?.teamId || !pruebaId) return undefined;
    const controller = new AbortController();
    setCandidates(null);
    getAthleticsRelayCandidates({ idevent, idsport, pruebaId, teamId: team.teamId, signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted && Array.isArray(data?.candidates)) setCandidates(data.candidates);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [open, team?.teamId, pruebaId, idevent, idsport]);

  const members = useMemo(() => {
    if (!candidates) return teamMembers;
    // Los miembros actuales que el servidor no devolvió (p. ej. ya sin inscripción vigente) se conservan.
    const known = new Set(candidates.map((candidate) => candidate.entryId));
    return [...candidates, ...teamMembers.filter((member) => !known.has(member.entryId))];
  }, [candidates, teamMembers]);

  useEffect(() => {
    if (!open) return;
    const initial = {};
    (legs || []).forEach((leg) => {
      initial[leg.leg] = String(leg.entryId);
    });
    setSelection(initial);
  }, [open, legs]);

  const chosen = Object.values(selection).filter(Boolean);
  const duplicated = chosen.length !== new Set(chosen).size;

  const takenElsewhere = (leg) =>
    new Set(LEGS.filter((other) => other !== leg && selection[other]).map((other) => selection[other]));

  const memberById = useMemo(
    () => new Map(members.map((member) => [String(member.entryId), member])),
    [members]
  );

  const confirm = () => {
    const next = LEGS.filter((leg) => selection[leg]).map((leg) => ({
      entryId: Number(selection[leg]),
      leg,
    }));
    onConfirm(next);
  };

  return (
    <Modal open={Boolean(open)} onClose={onClose}>
      <ModalDialog sx={{ width: "min(94vw, 520px)", borderRadius: "16px" }}>
        <ModalClose />
        <DialogTitle>Relevistas · {team?.name || "Equipo"}</DialogTitle>
        <DialogContent>
          {mixed ? (
            <p className="mb-3 flex items-start gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700">
              <FiInfo className="mt-0.5 shrink-0" aria-hidden="true" />
              Posta mixta: el orden sugerido es V-D-V-D (varón, dama, varón, dama).
            </p>
          ) : null}
          {!members.length ? (
            <p className="text-sm text-slate-500">No hay deportistas acreditados de esta institución para elegir.</p>
          ) : (
            <div className="space-y-2">
              {LEGS.map((leg) => {
                const current = memberById.get(selection[leg] || "");
                const expected = mixed ? MIXED_HINT[leg] : null;
                const mismatch = Boolean(expected && current?.gender && current.gender !== expected);
                const taken = takenElsewhere(leg);
                const options = members.map((member) => ({
                  value: String(member.entryId),
                  label: `${member.name}${member.gender ? ` (${member.gender})` : ""}${member.level ? ` · ${member.level}` : ""}`,
                  disabled: taken.has(String(member.entryId)),
                }));
                return (
                  <label key={leg} className="flex items-center gap-3">
                    <span className="w-20 shrink-0 text-sm font-bold text-slate-700">
                      Posta {leg}
                      {expected ? (
                        <span className="ml-1 text-[11px] font-semibold text-slate-400">({expected})</span>
                      ) : null}
                    </span>
                    <SearchableSelect
                      value={selection[leg] || ""}
                      options={options}
                      disabled={readOnly}
                      onChange={(next) => setSelection((prev) => ({ ...prev, [leg]: next }))}
                      className={`${selectClass} min-w-0 ${mismatch ? "ring-2 ring-amber-300" : ""}`}
                      ariaLabel={`Relevista de la posta ${leg}`}
                    />
                    {mismatch ? (
                      <span className="shrink-0 text-[11px] font-semibold text-amber-700" title="Orden sugerido V-D-V-D">
                        {GENDER_LABEL[current.gender] || current.gender}
                      </span>
                    ) : null}
                  </label>
                );
              })}
            </div>
          )}
          {duplicated ? (
            <p className="mt-3 text-xs font-semibold text-rose-600">Un mismo atleta no puede correr dos postas.</p>
          ) : null}
        </DialogContent>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="plain" color="neutral" onClick={onClose}>
            {readOnly ? "Cerrar" : "Cancelar"}
          </Button>
          {!readOnly ? (
            <Button variant="solid" color="primary" onClick={confirm} disabled={duplicated || !members.length}>
              Aplicar
            </Button>
          ) : null}
        </div>
      </ModalDialog>
    </Modal>
  );
};

export default RelayLegsModal;
